import { useState, useEffect, useCallback, useRef } from 'react';
import { useForm, useFieldArray, FieldErrors, UseFormReturn, FieldArrayWithId } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { v4 as uuidv4 } from 'uuid';
import { formSchema, type FormValues } from '../utils/validationSchema';
import useSaveQuestions from './useSaveQuestions';

import { QuestionType, type CampaignQuestion } from '../types';
import { AxiosError } from 'axios';

interface UseQuestionManagementProps {
    campaignId: number | null;
    initialQuestions?: CampaignQuestion[];
    onSaveSuccess?: () => void;
}

interface UseQuestionManagementResult {
    fields: FieldArrayWithId<FormValues, 'questions', 'fieldKey'>[];
    questionsToRemove: number[];
    errors: FieldErrors<FormValues>;
    isLoading: boolean;
    isError: boolean;
    error: AxiosError | null;
    handleAddQuestion: () => void;
    handleQuestionDelete: (index: number) => void;
    handleQuestionReorder: (activeIndex: number, overIndex: number) => void;
    handleSave: () => void;
    formMethods: UseFormReturn<FormValues>;
}

/**
 * Handles question management in the campaign questions editor form
 * like adding, editing, deleting and reordering of questions.
 */
const useQuestionManagement = ({
    campaignId,
    initialQuestions = [] as CampaignQuestion[],
    onSaveSuccess,
}: UseQuestionManagementProps): UseQuestionManagementResult => {
    const [questionsToRemove, setQuestionsToRemove] = useState<number[]>([]);
    const hasAddedInitialQuestion = useRef(false);

    const saveQuestionsMutation = useSaveQuestions();

    const formMethods = useForm<FormValues>({
        defaultValues: {
            questions: initialQuestions.length > 0 ? initialQuestions : [],
        },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        resolver: zodResolver(formSchema),
    });

    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors },
    } = formMethods;

    const { fields, append, remove, move } = useFieldArray({
        control,
        name: 'questions',
        keyName: 'fieldKey', // Use a different key to avoid overwriting the actual question id
    });

    // Reset the form when initialQuestions change
    useEffect(() => {
        if (initialQuestions.length > 0) {
            setValue('questions', initialQuestions);
            hasAddedInitialQuestion.current = true;
        } else if (initialQuestions.length === 0 && !hasAddedInitialQuestion.current) {
            // If there are no initial questions and we haven't added one yet, add an empty question
            const newQuestion: CampaignQuestion = {
                id: uuidv4(),
                type: QuestionType.TEXT,
                text: '',
                title: '',
            };

            append(newQuestion);
            hasAddedInitialQuestion.current = true;
        }
    }, [initialQuestions, setValue, append]);

    // Handles question addition
    const handleAddQuestion = useCallback(() => {
        const newQuestion: CampaignQuestion = {
            id: uuidv4(),
            type: QuestionType.TEXT,
            text: '',
            title: '',
        };

        append(newQuestion);
    }, [append]);

    // Handles question deletion
    const handleQuestionDelete = useCallback(
        (index: number) => {
            setQuestionsToRemove(prev => [...prev, index]);

            // Remove the question after animation completes
            setTimeout(() => {
                remove(index);
                setQuestionsToRemove(prev => prev.filter(idx => idx !== index));
            }, 300);
        },
        [remove]
    );

    // Handles question reordering
    const handleQuestionReorder = useCallback(
        (activeIndex: number, overIndex: number) => {
            if (activeIndex !== overIndex) {
                move(activeIndex, overIndex);
            }
        },
        [move]
    );

    // Handles form submission
    const onSubmit = useCallback(
        (data: FormValues) => {
            if (!campaignId) return;

            saveQuestionsMutation.mutate(
                { campaignId, questions: data.questions as CampaignQuestion[] },
                {
                    onSuccess: () => {
                        //@ TODO: Refetch the questions since response is not being returned
                        onSaveSuccess?.();
                    },
                }
            );
        },
        [campaignId, saveQuestionsMutation, onSaveSuccess]
    );

    // Handles save button click
    const handleSave = handleSubmit(onSubmit);

    return {
        fields,
        questionsToRemove,
        errors,
        isLoading: saveQuestionsMutation.isPending,
        isError: saveQuestionsMutation.isError,
        error: saveQuestionsMutation.error as AxiosError | null,
        handleAddQuestion,
        handleQuestionDelete,
        handleQuestionReorder,
        handleSave,
        formMethods,
    };
};

export default useQuestionManagement;
